import { OverlayScrollArea } from '@odo/shared-ui';
import type { RefObject } from 'react';
import type { RoleMember } from '../../services/roles';
import { AssigneeMemberRow } from './AssigneeMemberRow';
import { AssigneeSearchBox } from './AssigneeSearchBox';

interface AssigneeMenuListProps {
    isLoading: boolean;
    sortedMembers: RoleMember[];
    filteredMembers: RoleMember[];
    showSearch: boolean;
    search: string;
    onSearchChange: (value: string) => void;
    searchInputRef: RefObject<HTMLInputElement>;
    assignedTo: string | null;
    currentUserEmail: string;
    onAssign: (email: string) => void;
    onUnassign: () => void;
}

/** The FloatingMenu's contents: loading/empty states, member list, Unassign. */
export function AssigneeMenuList({
    isLoading,
    sortedMembers,
    filteredMembers,
    showSearch,
    search,
    onSearchChange,
    searchInputRef,
    assignedTo,
    currentUserEmail,
    onAssign,
    onUnassign
}: AssigneeMenuListProps) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-4">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            </div>
        );
    }

    if (sortedMembers.length === 0) {
        return <p className="text-xs text-gray-400 text-center py-3">No team members</p>;
    }

    return (
        <div className="flex flex-col gap-1 min-h-0">
            {showSearch && (
                <AssigneeSearchBox
                    value={search}
                    onChange={onSearchChange}
                    inputRef={searchInputRef}
                />
            )}
            {/* Capped + scrollable — all-ODO mode can list the whole company. */}
            <OverlayScrollArea
                className="flex flex-col min-h-0"
                contentClassName="max-h-60"
                innerClassName="flex flex-col gap-1"
            >
                {filteredMembers.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-3">No matches</p>
                )}
                {filteredMembers.map((member) => (
                    <AssigneeMemberRow
                        key={member.id}
                        member={member}
                        isSelf={member.email === currentUserEmail}
                        isActive={member.email === assignedTo}
                        onSelect={() => onAssign(member.email)}
                    />
                ))}
            </OverlayScrollArea>

            {assignedTo && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onUnassign();
                    }}
                    className="flex items-center gap-2.5 w-full text-xs font-semibold py-[9px] px-3.5 rounded-lg text-red-500 bg-red-50 hover:bg-red-100 hover:scale-[1.02] transition-all duration-100 mt-1"
                >
                    <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 14 14" fill="none">
                        <path
                            d="M3.5 3.5L10.5 10.5M3.5 10.5L10.5 3.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                    </svg>
                    Unassign
                </button>
            )}
        </div>
    );
}
